import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DecisionTree } from '../models/decisionTree';
import { DecisionProvider, useDecisionContext } from '../context/DecisionContext';
import DecisionTreeView from './DecisionTreeView';
import { ArrowLeftIcon, DocumentIcon, DocumentPlusIcon } from '@heroicons/react/24/outline';

// 用于显示保存的决策树的内部组件
const DecisionTreeLoader: React.FC = () => {
  const { setDecisionTree, loading, error, saveDecisionTree } = useDecisionContext();
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [decisionContext, setDecisionContext] = useState<any>(null);

  // 创建一个自定义的保存函数，能够更新现有记录而不是创建新记录
  const saveDecisionWithContext = useCallback(() => {
    if (decisionContext && decisionContext.recordId) {
      console.log('使用上下文保存决策树，记录ID:', decisionContext.recordId);
      // 使用会话存储中的上下文数据更新全局状态
      if (decisionContext.question) {
        sessionStorage.setItem('currentQuestion', decisionContext.question);
      }
      
      // 如果有上下文信息，则使用已有的记录ID进行更新
      saveDecisionTree();
    } else {
      console.log('普通保存决策树，无记录ID');
      // 否则正常保存
      saveDecisionTree();
    }
  }, [decisionContext, saveDecisionTree]);

  // 将saveDecisionWithContext函数暴露给外部访问
  useEffect(() => {
    // 使用自定义属性将方法暴露给父组件
    const loaderElement = document.querySelector('.decision-tree-loader');
    if (loaderElement) {
      (loaderElement as any).saveDecisionWithContext = saveDecisionWithContext;
    }
  }, [saveDecisionWithContext]);

  // 只在组件挂载时执行一次，而不是每次渲染
  useEffect(() => {
    // 从会话存储中获取决策树并设置到上下文中
    const loadDecisionTree = () => {
      const savedTreeJson = sessionStorage.getItem('viewingDecisionTree');
      const contextJson = sessionStorage.getItem('viewingDecisionContext');
      
      if (savedTreeJson) {
        try {
          const parsedTree = JSON.parse(savedTreeJson);
          
          // 保存决策上下文
          if (contextJson) {
            const context = JSON.parse(contextJson);
            setDecisionContext(context);
            
            // 将问题存储在会话存储中，以便在保存时使用
            if (context.question) {
              sessionStorage.setItem('currentQuestion', context.question);
            }
            
            console.log('加载决策上下文:', context);
          }
          
          // 设置到DecisionContext中
          setDecisionTree(parsedTree);
          setIsLoaded(true);
        } catch (err) {
          setLoadError('无法解析决策树数据');
          console.error('解析决策树数据失败:', err);
        }
      } else {
        setLoadError('未找到决策树数据');
      }
    };

    loadDecisionTree();
    // 依赖为空数组，确保只在挂载时调用一次
  }, [setDecisionTree]);

  if (loadError) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">出错了</h3>
            <div className="mt-2 text-sm text-red-700">{loadError}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded && !loading) {
    return (
      <div className="text-center py-10 text-gray-500">
        没有可显示的决策树数据
      </div>
    );
  }

  // 如果是从记录中加载的决策树，则显示决策上下文信息
  if (decisionContext) {
    return (
      <div className="w-full decision-tree-loader">
        {decisionContext.tags && decisionContext.tags.length > 0 && (
          <div className="bg-gray-50 p-4 mb-4 rounded-md border border-gray-200">
            <div className="flex flex-wrap gap-2 mb-2">
              <span className="text-sm text-gray-600">标签:</span>
              {decisionContext.tags.map((tag: string) => (
                <span key={tag} className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                  {tag}
                </span>
              ))}
            </div>
            <div className="text-sm text-gray-600">
              保存时间: {new Date(decisionContext.timestamp).toLocaleString()}
            </div>
          </div>
        )}
        <DecisionTreeView />
      </div>
    );
  }

  return <div className="decision-tree-loader"><DecisionTreeView /></div>;
};

// ViewDecisionPage组件显示保存的决策树
const ViewDecisionPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/records')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            返回列表
          </button>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <DocumentIcon className="h-4 w-4 mr-2" />
            新建决策
          </button>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <h3 className="text-lg leading-6 font-medium text-gray-900">决策树详情</h3>
          <SaveDecisionButton />
        </div>
        <div className="border-t border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <DecisionTreeLoader />
          </div>
        </div>
      </div>
    </div>
  );
};

// 新增组件，用于显示保存按钮
const SaveDecisionButton: React.FC = () => {
  const loaderRef = useRef<HTMLDivElement>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { saveDecisionTree } = useDecisionContext();
  
  // 使用ref获取DecisionTreeLoader组件的实例
  useEffect(() => {
    const loaderElement = document.querySelector('.decision-tree-loader');
    if (loaderElement) {
      loaderRef.current = loaderElement as HTMLDivElement;
    }
  }, []);
  
  const handleSave = () => {
    // 通过ref获取DecisionTreeLoader组件的saveDecisionWithContext方法
    const saveDecisionWithContext = (loaderRef.current as any)?.saveDecisionWithContext;
    if (typeof saveDecisionWithContext === 'function') {
      saveDecisionWithContext();
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 3000);
    } else {
      // 如果无法访问自定义保存函数，则使用常规方法保存
      saveDecisionTree();
      setShowConfirmation(true);
      setTimeout(() => setShowConfirmation(false), 3000);
    }
  };
  
  return (
    <div className="relative">
      <button
        onClick={handleSave}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
      >
        <DocumentPlusIcon className="h-4 w-4 mr-2" />
        保存决策
      </button>
      
      {showConfirmation && (
        <div className="absolute right-0 mt-2 w-48 bg-green-100 border border-green-200 rounded-md p-2 text-sm text-green-800">
          决策已成功保存
        </div>
      )}
    </div>
  );
};

export default ViewDecisionPage; 